import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { keys } from '~/api/hooks'
import { dropPrivateSession } from './session'

/**
 * Passwordless spec §2 (owner, 2026-11-03; rulings P5, P11): a decided `approved` or `review` registration becomes the
 * voter session (`POST /api/member/session/claim`) and the voter lands on the voter page, signed in. The registration
 * cache belongs to the registration cookie that the claim ends, so it is dropped before the voter page loads.
 *
 * - 200: the voter page (`/me`), with the voter's card already in the cache when it could be read.
 * - 409 `invalid_state_transition`: not claimable after all; the caller keeps the registration page.
 * - 401 `invalid_registration`: the registration ended here (claimed on another device); the caller offers the
 *   sign-in email.
 */
export function useSessionClaim() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async () => {
      await api('memberSessionClaim')
      // The voter page reads the card itself when this fails; the claim has already succeeded.
      return api('memberMe').catch(() => null)
    },
    onSuccess: (me) => {
      dropPrivateSession(qc)
      if (me) qc.setQueryData(keys.me, me)
      navigate('/me', { replace: true })
    },
  })
}

export type SessionClaim = ReturnType<typeof useSessionClaim>

/** The claim answered "the registration ended here" (401): the page offers the sign-in email instead. */
export const claimEnded = (claim: SessionClaim) =>
  claim.isError && isApiError(claim.error) && claim.error.status === 401

/** The claim answered "not claimable" (409): the registration pages stay. */
export const claimRefused = (claim: SessionClaim) =>
  claim.isError && isApiError(claim.error) && claim.error.status === 409

/**
 * Claims once, by itself (no button), as soon as `claimable` is true: on the status page when the decision is there
 * or arrives, and when a voter comes back with the registration cookie still valid.
 */
export function useAutoClaim(claimable: boolean, claim: SessionClaim) {
  const started = useRef(false)
  const { mutate } = claim
  useEffect(() => {
    if (!claimable || started.current) return
    started.current = true
    mutate()
  }, [claimable, mutate])
}

/** Where the sign-in page says "your registration is finished here" (a claim or a status read answered 401). */
export const REGISTRATION_ENDED_STATE = { registrationEnded: true } as const
